import { Zap, CheckCircle2, Circle, Calendar, Rocket, Users, Globe, Code, Droplets, TrendingDown, Lock } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";

const milestones = [
  {
    phase: "Phase 1",
    title: "Presale Launch",
    description: "48-hour presale with 40% discount. 100% of presale funds go directly into LP.",
    status: "active",
    date: "Monday (48 hours)",
    icon: Rocket,
    details: [
      "48-hour presale window",
      "40% discount vs. initial listing",
      "200M tokens available",
      "100% funds to liquidity",
    ],
  },
  {
    phase: "Phase 2",
    title: "Token Launch & LP Deployment",
    description: "Official launch on Thursday, December 11th. Phased LP deployment on Meteora for price stability.",
    status: "upcoming",
    date: "Dec 11, 2025",
    icon: Droplets,
    details: [
      "Launch on Meteora DEX",
      "Initial LP: $50k + 200M tokens",
      "Phased deployment: 20% per day for 5 days",
      "LP fees used for buybacks & burns",
    ],
  },
  {
    phase: "Phase 3",
    title: "Airdrop Distribution",
    description: "500M tokens distributed across Platinum, Purple, and Black tiers",
    status: "upcoming",
    date: "Post-Launch",
    icon: Zap,
    details: [
      "Platinum: 1,640,000 $PULSE",
      "Purple: 420,000 $PULSE",
      "Black: 90,000 $PULSE",
      "Community-wide distribution",
    ],
  },
  {
    phase: "Phase 4",
    title: "Buyback & Burn Program",
    description: "First 3 months: 90% of PulseAPP revenue + 90% of Pulse Cards royalties for buybacks. Then 50% ongoing.",
    status: "upcoming",
    date: "Months 1-3",
    icon: TrendingDown,
    details: [
      "90% revenue/royalties for buybacks",
      "Automatic burn mechanism",
      "Price stability & deflationary pressure",
      "Sustainable tokenomics",
    ],
  },
  {
    phase: "Phase 5",
    title: "Long-Term Sustainability",
    description: "Team tokens vested for 1 year. Ongoing 50% revenue share for buybacks & burns.",
    status: "upcoming",
    date: "Ongoing",
    icon: Lock,
    details: [
      "Team allocation: 1-year vesting",
      "50% ongoing revenue share",
      "Continuous buybacks & burns",
      "Long-term value creation",
    ],
  },
];

export function Roadmap() {
  const [visibleIndexes, setVisibleIndexes] = useState<Set<number>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute("data-index") || "0");
            setVisibleIndexes((prev) => new Set([...prev, index]));
          }
        });
      },
      { threshold: 0.2 }
    );

    milestones.forEach((_, index) => {
      const element = document.getElementById(`milestone-${index}`);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      milestones.forEach((_, index) => {
        const element = document.getElementById(`milestone-${index}`);
        if (element) {
          observer.unobserve(element);
        }
      });
    };
  }, []);

  return (
    <div className="w-full py-12 md:py-16 px-4 bg-gradient-to-b from-purple-900/5 to-transparent">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Zap className="w-8 h-8 text-purple-400" />
            <h2 className="text-3xl md:text-4xl font-bold text-white">Roadmap</h2>
          </div>
          <p className="text-gray-400 max-w-2xl mx-auto text-base md:text-lg">
            Our strategic journey to revolutionize the Solana DeFi ecosystem.
            Built for the long run - sustainable, transparent, and designed to reward holders at every step.
          </p>
        </div>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 md:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-500 via-violet-500 to-indigo-500 hidden md:block" />

          <div className="space-y-8 md:space-y-12">
            {milestones.map((milestone, index) => {
              const Icon = milestone.icon;
              const isVisible = visibleIndexes.has(index);
              const isActive = milestone.status === "active";

              return (
                <div
                  key={index}
                  id={`milestone-${index}`}
                  data-index={index}
                  className={`relative flex flex-col md:flex-row items-start gap-4 md:gap-6 transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                    }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {/* Icon */}
                  <div className="relative z-10 flex-shrink-0">
                    {isActive ? (
                      <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-purple-600 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-500/50 transform transition-all duration-300 hover:scale-110">
                        <Icon className="w-6 h-6 md:w-8 md:h-8 text-white" fill="white" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gray-800 border-2 border-purple-500/30 flex items-center justify-center transition-all duration-300 hover:border-purple-500/50 hover:scale-110">
                        <Icon className="w-6 h-6 md:w-8 md:h-8 text-purple-500/50" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <Card
                    className={`flex-1 bg-card/50 backdrop-blur-sm border-purple-500/20 transition-all duration-300 hover:border-purple-500/40 hover:shadow-lg hover:shadow-purple-500/10 ${isActive
                        ? "border-purple-500/50 shadow-lg shadow-purple-500/20"
                        : ""
                      }`}
                  >
                    <CardContent className="pt-6 px-4 md:px-6">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs md:text-sm font-semibold text-purple-400 bg-purple-500/10 px-2 py-1 rounded">
                            {milestone.phase}
                          </span>
                          {isActive && (
                            <span className="text-xs bg-green-500/20 text-green-400 px-2 py-1 rounded flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3" />
                              Active
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 text-xs md:text-sm text-gray-500">
                          <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                          <span>{milestone.date}</span>
                        </div>
                      </div>
                      <h3 className="text-xl md:text-2xl font-bold text-white mb-2 flex items-center gap-2">
                        {milestone.title}
                        {isActive && <Zap className="w-5 h-5 text-purple-400" />}
                      </h3>
                      <p className="text-gray-400 mb-4 text-sm md:text-base">{milestone.description}</p>

                      {/* Details */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
                        {milestone.details.map((detail, detailIndex) => (
                          <div
                            key={detailIndex}
                            className="flex items-center gap-2 text-xs md:text-sm text-gray-400"
                          >
                            <div className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                            <span>{detail}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>

        {/* LP Deployment Strategy Info */}
        <Card className="bg-card/50 backdrop-blur-sm border-purple-500/20 mt-12">
          <CardContent className="pt-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Droplets className="w-6 h-6 text-purple-400" />
              LP Deployment Strategy
            </h3>
            <div className="space-y-4 text-sm md:text-base text-gray-400">
              <p>
                To prevent airdrop recipients and presalers from draining liquidity instantly,
                our LP will be deployed on <strong className="text-purple-400">Meteora</strong> with a phased approach:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong className="text-purple-300">Only 20% of the LP</strong> will be added per day for 5 days
                </li>
                <li>
                  <strong className="text-purple-300">All LP fees</strong> generated during this phased deployment
                  will be used to buy and burn $PULSE
                </li>
                <li>
                  This structure ensures that for the first 5 days, the token price will remain at least
                  at the promised initial price and can go higher naturally
                </li>
                <li>
                  The calculation is based solely on the guaranteed $50k LP provided by the team,
                  not on presale funds
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
